import { lazy } from 'react';
import { AppRoute } from '../../types/routeTypes';
import { role } from '../constants';

// Lazy load pages
const NotFound = lazy(() => import('../../components/NotFound'));
const Login = lazy(() => import('../../pages/accounts/Login'));
const Register = lazy(() => import('../../pages/accounts/register/Register'));
const InviteUser = lazy(() => import('../../pages/users/InviteUser'));
const ReferralDecision = lazy(() => import('../../pages/ReferralDecision'));
const AdminReview = lazy(() => import('../../pages/AdminReview'));
const ReferralList = lazy(() => import('../../pages/review/ReferralList'));
const UserDetailsView = lazy(() => import('../../pages/users/UserDetailsView'));
const RegisterManager = lazy(() => import('../../pages/accounts/register/RegisterManager'));
const SuperAdminApprovalDashboard = lazy(() => import('../../pages/SuperAdminApprovalDashboard'));
const Users = lazy(() => import('../../pages/users/Users'));
const UserView = lazy(() => import('../../pages/users/UserView'));
const DeleteRequestsManagement = lazy(() => import('../../pages/review/DeleteRequestsManagement'));
const Profile = lazy(() => import('../../pages/accounts/Profile'));
const NotificationSettings = lazy(() => import('../../pages/accounts/NotificationSettings'));
const ReviewDashboard = lazy(() => import('../../pages/review/ReviewDashboard'));
const EventList = lazy(() => import('../../pages/events/EventList'));
const StudiosList = lazy(() => import('../../pages/studio/Studios'));
const StudioDetails = lazy(() => import('../../pages/studio/StudioDetails'));
const StudioDetailsView = lazy(() => import("../../pages/studio/StudioDetailsView"));
const AvailabilityPage = lazy(() => import('../../pages/AvailabilityPage'));
const Dashboard = lazy(() => import('../../pages/dashboard/DashboardList'));
const EnquiryList = lazy(() => import('../../pages/enquiry/EnquiryList'));
const EnquiryDetails = lazy(() => import('../../pages/enquiry/EnquiryDetails'));
const EventLayout = lazy(() => import('../../pages/events/eventManagement/EventLayout'));
const CustomerAlbumReviewScreen = lazy(() => import('../../pages/review/CustomerAlbumReviewScreen'));
const CustomerEventAlbumProofPage = lazy(() => import('../../pages/review/CustomerEventAlbumProofPage'));
const AlbumBuilderPage = lazy(() => import('../../pages/events/albumBuilder/AlbumBuilderPage'));
const AlbumTemplateListPage = lazy(() => import('../../pages/albumTemplates/AlbumTemplateListPage'));
const CreateTemplatePage = lazy(() => import('../../pages/albumTemplates/CreateTemplatePage'));
const LandingPage = lazy(() => import('../../pages/LandingPage'));
const TermsOfServicePage = lazy(() => import('../../pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('../../pages/PrivacyPolicyPage'));
const SubscriptionPlanList = lazy(() => import('../../pages/subscriptionPlans/listSubscriptionPlans'));
const SubscriptionPlanForm = lazy(() => import('../../pages/subscriptionPlans/createSubscriptionPlan'));
const ViewSubscription = lazy(() => import('../../pages/subscriptionPlans/viewSubscription'));
const ReleaseNotes = lazy(() => import('../../pages/releaseNotes'));
const SubscriptionCenter = lazy(() => import('../../pages/studioSubscription/subscription'));
const RouteManagement = lazy(() => import('../../pages/route/routes'));
const NotificationView = lazy(() => import('../../pages/accounts/NotificationView'));
const Documentation = lazy(() => import('../../pages/General/Documention'));
import NotAccessible from "@/components/NotAccessible";

export const appRoutes: AppRoute[] = [
    // Public Routes
    {
        key: 'landing',
        label: 'Landing Page',
        path: '/',
        component: LandingPage,
    },
    {
        key: 'terms-of-service',
        label: 'Terms of Service',
        path: '/terms-of-service',
        component: TermsOfServicePage,
        isPublic: true,
        isCommon: true,
    },
    {
        key: 'privacy-policy',
        label: 'Privacy Policy',
        path: '/privacy-policy',
        component: PrivacyPolicyPage,
        isPublic: true,
        isCommon: true,
    },
    {
        key: 'login',
        label: 'Login',
        path: '/login',
        component: Login,
    },
    {
        key: 'register',
        label: 'Register',
        path: '/register',
        component: Register,
        children: [
            {
                key: 'register-user',
                label: 'Register user',
                path: '/register/:user',
                component: RegisterManager,
            },
        ],
    },
    {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        component: Dashboard,
    },
    {
        key: 'profile',
        label: 'Profile',
        path: '/profile',
        component: Profile,
    },
    {
        key: 'notification-settings',
        label: 'Notification Settings',
        path: '/notifications-settings',
        component: NotificationSettings,
    },
    {
        key: 'notification-view',
        label: 'Notification View',
        path: '/notifications/view',
        component: NotificationView,
    },
    {
        key: 'users',
        label: 'Users',
        path: '/users',
        children: [
            {
                key: 'users-list',
                label: 'Users List',
                path: '/users',
                component: Users,
            },
            {
                key: 'users-view',
                label: 'View User',
                path: '/users/view',
                component: UserView,

            },
            {
                key: 'users-edit',
                label: 'Edit User',
                path: '/users/edit',
                component: UserView,
            },
            {
                key: 'users-invite',
                label: 'Invite User',
                path: '/users/invite',
                component: InviteUser,
            },
            {
                key: 'users-view-subscription',
                label: 'View User Subscription',
                path: '/users/view-subscription',
                component: ViewSubscription,
            },
        ],
    },
    {
        key: 'events',
        label: 'Events',
        path: '/events',
        children: [
            {
                key: 'events-list',
                label: 'Events List',
                path: '/events',
                component: EventList,
            },
            {
                key: 'events-management',
                label: 'Event Management',
                path: '/events/management',
                component: EventLayout,
                noFooter: true,
            },
        ],
    },
    {
        key: 'album-templates',
        label: 'Templates',
        path: '/album-templates',
        children: [
            {
                key: 'album-templates-list',
                label: 'Album Templates List',
                path: '/album-templates',
                component: AlbumTemplateListPage,
            },
            {
                key: 'album-templates-create',
                label: 'Create Album Template',
                path: '/album-templates/create',
                component: CreateTemplatePage,
            },
            {
                key: 'album-templates-edit',
                label: 'Edit Album Template',
                path: '/album-templates/edit',
                component: CreateTemplatePage,
            },
        ],
    },
    {
        key: 'album-builder-template',
        label: 'Album Builder Template',
        path: '/album-builder/template',
        component: AlbumBuilderPage,
    },
    {
        key: 'album-builder-template',
        label: 'Album Builder Template',
        path: '/album-builder/preview',
        component: AlbumBuilderPage,
    },
    {
        key: 'album-builder-album',
        label: 'Album Builder Album',
        path: '/album-builder/album/',
        component: AlbumBuilderPage,
        noFooter: true,
    },
    {
        key: 'album-review',
        label: 'Album Review',
        path: '/album-review',
        component: CustomerAlbumReviewScreen,
    },
    {
        key: 'view-album',
        label: 'View Album',
        path: '/view-album',
        component: CustomerEventAlbumProofPage,
    },
    {
        key: 'availability',
        label: 'Availability',
        path: '/availability',
        component: AvailabilityPage,
    },
    {
        key: 'review',
        label: 'Review',
        path: '/review',
        component: ReviewDashboard,
    },
    {
        key: 'referrals',
        label: 'Referrals',
        path: '/referrals',
        component: ReferralList,

    },
    {
        key: 'application-review',
        label: 'Application Review',
        path: '/application-review',
        component: UserDetailsView,
    },
    {
        key: 'referral-decision',
        label: 'Referral Decision',
        path: '/referral-decision',
        component: ReferralDecision,
    },
    {
        key: 'admin-review',
        label: 'Admin Review',
        path: '/admin-review',
        component: AdminReview,
    },
    {
        key: 'admin-approvals',
        label: 'Admin Approvals',
        path: '/admin/approvals',
        component: SuperAdminApprovalDashboard,
    },
    {
        key: 'admin-delete-requests',
        label: 'Delete Requests Management',
        path: '/admin/delete-requests',
        component: DeleteRequestsManagement,
    },
    {
        key: 'studios',
        label: 'Studios',
        path: '/studios',
        children: [
            {
                key: 'studios-list',
                label: 'Studios List',
                path: '/studios',
                component: StudiosList,
                allowedRoles: [role.SUPER_ADMIN_ROLE],
            },
            {
                key: 'studios-view',
                label: 'View Studio',
                path: '/studios/view',
                component: StudioDetailsView,
            },
            {
                key: 'studios-edit',
                label: 'Edit Studio',
                path: '/studios/edit',
                component: StudioDetails,
            },
            {
                key: 'studios-view-subscription',
                label: 'View Studio Subscription',
                path: '/studios/view-subscription',
                component: ViewSubscription,
            },
        ],
    },
    {
        key: 'enquiries',
        label: 'Enquiries',
        path: '/enquiries',
        children: [
            {
                key: 'enquiries-list',
                label: 'Enquiries List',
                path: '/enquiries',
                component: EnquiryList,
            },
            {
                key: 'enquiries-create',
                label: 'Create Enquiry',
                path: '/enquiries/create',
                component: EnquiryDetails,
            },
            {
                key: 'enquiries-view',
                label: 'View Enquiry',
                path: '/enquiries/view',
                component: EnquiryDetails,
            },
            {
                key: 'enquiries-edit',
                label: 'Edit Enquiry',
                path: '/enquiries/edit',
                component: EnquiryDetails,
            },
        ],
    },
    {
        key: 'subscription-plans',
        label: 'Subscriptions',
        path: '/subscription-plans',
        children: [
            {
                key: 'subscription-plans-list',
                label: 'Subscription Plans List',
                path: '/subscription-plans',
                component: SubscriptionPlanList,
            },
            {
                key: 'subscription-plans-create',
                label: 'Create Subscription Plan',
                path: '/subscription-plans/create',
                component: SubscriptionPlanForm,
            },
            {
                key: 'subscription-plans-view',
                label: 'View Subscription Plan',
                path: '/subscription-plans/view',
                component: SubscriptionPlanForm,
            },
        ],
    },
    {
        key: 'subscription',
        label: 'Subscription Center',
        path: '/subscription',
        component: SubscriptionCenter,
    },
    {
        key: 'release-notes',
        label: 'Release Notes',
        path: '/release-notes',
        component: ReleaseNotes,
    },
    {
        key: 'not-found',
        label: 'Not Found',
        path: '*',
        component: NotFound,
    },
    {
        key: 'route-management',
        label: 'Routes',
        path: '/routes',
        component: RouteManagement,
    },
    {
        key: 'documentation',
        label: 'Documentation',
        path: '/documentation',
        component: Documentation,
    },
    {
        key: 'not-accessible',
        label: 'Not Accessible',
        path: '/not-accessible',
        component: NotAccessible,
    },
];